import { AddLink, Check, CheckCircle } from "@mui/icons-material";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import EditableBox from "components/EditableBox";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HRToRequisitionLinkExists } from "services/api";
import { addLinkHRToRequisition } from "services/api";
import { hiringReasonOptions } from "services/utils";
import { formatEnum } from "utils";
const getStatusColor = (status) => {
  if (status === "COMPLETED") return "#2196f3"; // 🔵 Blue (Material UI Info color)
  if (status === "APPROVED") return "#4caf50"; // 🟢 Green
  if (status === "REJECTED") return "#f44336"; // 🔴 Red
  if (status === "PENDING_HR_APPROVAL") return "#ffb300"; // 🟠 Amber
  if (status === "PENDING_DIRECTOR_APPROVAL") return "#ff9800"; // 🟧 Orange
  if (status === "PENDING_CEO_APPROVAL") return "#9c27b0"; // 🟣 Purple
  return "#9e9e9e"; // ⚪ Grey
};

const RequisitionDetailsData = ({
  selectedReq,
  hrApprover,
  directorApprover,
  onSaveChanges,
  onStatusChange,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const [assigningHr, setAssigningHr] = useState(false);
  const [hrAssigned, setHrAssigned] = useState(false);
  const [checkingHrLink, setCheckingHrLink] = useState(true);
  const handleAssignHr = async () => {
    try {
      setAssigningHr(true);

      const response = await addLinkHRToRequisition(selectedReq.id, employee.id);

      if (response === "Assignment created successfully") {
        setHrAssigned(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAssigningHr(false);
    }
  };

  useEffect(() => {
    const checkHrLink = async () => {
      if (employee.isAdmin) {
        try {
          setCheckingHrLink(true);

          const exists = await HRToRequisitionLinkExists(selectedReq.id, employee.id);

          setHrAssigned(exists === true);
        } catch (e) {
          console.error("Error checking HR link", e);
        } finally {
          setCheckingHrLink(false);
        }
        if (selectedReq?.id && employee?.id) {
          checkHrLink();
        }
      }
    };
  }, [selectedReq?.id, employee?.id]);

  const handleFieldSave = (field, newValue) => {
    // Optimistic update or API call here
    onSaveChanges({ ...selectedReq, [field]: newValue });
  };

  const labelMap = {
    PENDING_HR_APPROVAL: t("admin.talentAcquisition.statusLabels.PENDING_HR_APPROVAL"),
    PENDING_DIRECTOR_APPROVAL: t("admin.talentAcquisition.statusLabels.PENDING_DIRECTOR_APPROVAL"),
    PENDING_CEO_APPROVAL: t("admin.talentAcquisition.statusLabels.PENDING_CEO_APPROVAL"),
    APPROVED: t("admin.talentAcquisition.statusLabels.APPROVED"),
    REJECTED: t("admin.talentAcquisition.statusLabels.REJECTED"),
    COMPLETED: t("admin.talentAcquisition.statusLabels.COMPLETED"),
  };

  return (
    <Grid container spacing={3} alignItems="stretch">
      <Grid item xs={12}>
        <Card>
          <CardContent>
            {employee.isAdmin ? (
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  {t("admin.requisitionDetails.jobInformation")}
                </Typography>

                {checkingHrLink ? (
                  <CircularProgress size={24} />
                ) : hrAssigned ? (
                  <Tooltip title="HR linked successfully">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{
                        color: "success.main",
                      }}
                    >
                      <Check fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>
                        +
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title="Assign HR to requisition">
                    <span>
                      <IconButton
                        onClick={handleAssignHr}
                        disabled={assigningHr}
                        sx={{
                          color: "primary.main",
                        }}
                      >
                        {assigningHr ? <CircularProgress size={20} /> : <AddLink />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  {t("admin.requisitionDetails.jobInformation")}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <b>{t("admin.requisitionDetails.jobTitle")}:</b> {selectedReq.jobTitle}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <b>{t("admin.requisitionDetails.department")}:</b> {selectedReq.departmentName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <b>{t("admin.requisitionDetails.branch")}:</b> {selectedReq.branchName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <b>{t("admin.requisitionDetails.employmentType")}:</b>{" "}
                  {formatEnum(selectedReq.employmentType)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <b>{t("admin.requisitionDetails.positions")}:</b> {selectedReq.numberOfPositions}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            {/* STATUS HEADER WITH COMPLETED BUTTON */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
              {/* Colored Status Banner */}
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                  mb: 2,
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: getStatusColor(selectedReq.status), // Ensure this function is available
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
                {/* {selectedReq.status?.replace(/_/g, " ")} */}
                {labelMap[selectedReq.status] || selectedReq.status}
              </Box>

              {/* 👇 "Mark as Completed" Button */}
              {selectedReq.status === "APPROVED" && canEdit && (
                <MDButton
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => onStatusChange("COMPLETED")}
                  sx={{ mt: 1 }}
                >
                  {t("admin.formCreation.markCompleted")} {/* Add translation key */}
                </MDButton>
              )}
            </Box>

            <Typography variant="h6" gutterBottom fontWeight={600}>
              {t("admin.requisitionDetails.approvals")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {/* HR */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("admin.requisitionDetails.hrApproval")}
                </Typography>
                <Typography>
                  <b>{t("admin.requisitionDetails.status")}:</b>{" "}
                  {selectedReq.hrApprovedBy ? (
                    <span style={{ color: "#4caf50" }}>
                      {t("admin.requisitionDetails.approved")}
                    </span>
                  ) : (
                    <span style={{ color: "#ffb300" }}>
                      {t("admin.requisitionDetails.pending")}
                    </span>
                  )}
                </Typography>

                <Typography>
                  <b>{t("admin.requisitionDetails.approvedBy")}:</b> {hrApprover?.name || "—"}
                </Typography>
                <Typography>
                  <b>{t("admin.requisitionDetails.at")}:</b> {selectedReq.hrApprovedAt || "—"}
                </Typography>
                <Box mt={1}>
                  <Typography fontWeight={600}>{t("admin.requisitionDetails.comment")}:</Typography>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      color: "#555",
                    }}
                  >
                    {selectedReq.hrComment || "—"}
                  </Typography>
                </Box>
              </Grid>

              {/* Director */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("admin.requisitionDetails.directorApproval")}
                </Typography>
                <Typography>
                  <b>{t("admin.requisitionDetails.status")}:</b>{" "}
                  {selectedReq.directorApproved ? (
                    <span style={{ color: "#4caf50" }}>
                      {t("admin.requisitionDetails.approved")}
                    </span>
                  ) : (
                    <span style={{ color: "#ff9800" }}>
                      {t("admin.requisitionDetails.pending")}
                    </span>
                  )}
                </Typography>

                <Typography>
                  <b>{t("admin.requisitionDetails.at")}:</b> {selectedReq.directorApprovedAt || "—"}
                </Typography>
                <Box mt={1}>
                  <Typography fontWeight={600}>{t("admin.requisitionDetails.comment")}:</Typography>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      color: "#555",
                    }}
                  >
                    {selectedReq.directorComment || "—"}
                  </Typography>
                </Box>
              </Grid>

              {/* CEO */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("admin.requisitionDetails.ceoApproval")}
                </Typography>
                <Typography>
                  <b>{t("admin.requisitionDetails.status")}:</b>{" "}
                  {selectedReq.ceoApproved ? (
                    <span style={{ color: "#4caf50" }}>
                      {t("admin.requisitionDetails.approved")}
                    </span>
                  ) : (
                    <span style={{ color: "#9c27b0" }}>
                      {t("admin.requisitionDetails.pending")}
                    </span>
                  )}
                </Typography>

                <Typography>
                  <b>{t("admin.requisitionDetails.at")}:</b> {selectedReq.ceoApprovedAt || "—"}
                </Typography>
                <Box mt={1}>
                  <Typography fontWeight={600}>{t("admin.requisitionDetails.comment")}:</Typography>
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      color: "#555",
                    }}
                  >
                    {selectedReq.ceoComment || "—"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* REQUEST DETAILS */}
      <Grid item xs={12} md={12}>
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              {t("admin.requisitionDetails.requestDetails")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ mb: 1 }}>
              <b>{t("admin.requisitionDetails.requestedBy")}:</b> {selectedReq.requestedByName}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {t("admin.requisitionDetails.reason")}
              </Typography>

              <Autocomplete
                options={hiringReasonOptions}
                value={
                  hiringReasonOptions.includes(selectedReq.reasonForHiring)
                    ? selectedReq.reasonForHiring
                    : "Other"
                }
                disabled={!canEdit}
                onChange={(event, newValue) => {
                  if (newValue === "Other") {
                    handleFieldSave("reasonForHiring", "");
                  } else {
                    handleFieldSave("reasonForHiring", newValue || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "transparent",
                        borderRadius: 2,
                        padding: 0,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                    {...params}
                    fullWidth
                    size="small"
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    padding: 1.5,
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    border: "1px solid transparent",
                    transition: "all 0.2s",
                  },
                  "&:hover .MuiAutocomplete-inputRoot": {
                    bgcolor: "grey.200",
                    border: "1px solid #bdbdbd",
                  },
                }}
              />

              {(selectedReq.reasonForHiring === "" ||
                !hiringReasonOptions.includes(selectedReq.reasonForHiring)) && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  sx={{ mt: 2 }}
                  label={t("admin.talent.specifyReason")}
                  value={selectedReq.reasonForHiring || ""}
                  disabled={!canEdit}
                  onChange={(e) => handleFieldSave("reasonForHiring", e.target.value)}
                />
              )}
            </Box>

            {/* 👇 2. EDITABLE DESCRIPTION */}
            <EditableBox
              label={t("admin.requisitionDetails.description")}
              value={selectedReq.description}
              onSave={(val) => handleFieldSave("description", val)}
              isEditable={canEdit}
            />

            {/* 👇 3. EDITABLE SKILLS */}
            <EditableBox
              label={t("admin.requisitionDetails.requiredSkills")}
              value={selectedReq.requiredSkills}
              onSave={(val) => handleFieldSave("requiredSkills", val)}
              isEditable={canEdit}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* APPROVALS SECTION */}
    </Grid>
  );
};

RequisitionDetailsData.propTypes = {
  selectedReq: PropTypes.shape({
    id: PropTypes.number.isRequired,
    jobTitle: PropTypes.string.isRequired,
    departmentName: PropTypes.string.isRequired,
    branchName: PropTypes.string.isRequired,
    employmentType: PropTypes.string,
    numberOfPositions: PropTypes.number.isRequired,
    requestedByName: PropTypes.string.isRequired,
    reasonForHiring: PropTypes.string,
    description: PropTypes.string,
    requiredSkills: PropTypes.string,
    status: PropTypes.string.isRequired,
    hrApprovedBy: PropTypes.string,
    hrApprovedAt: PropTypes.string,
    hrComment: PropTypes.string,
    directorApprovedAt: PropTypes.string,
    directorApproved: PropTypes.bool,
    directorComment: PropTypes.string,
    ceoApproved: PropTypes.bool,
    ceoApprovedAt: PropTypes.string,
    ceoComment: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  hrApprover: PropTypes.shape({
    name: PropTypes.string,
  }),
  directorApprover: PropTypes.shape({
    name: PropTypes.string,
  }),
  onSaveChanges: PropTypes.func,
  onStatusChange: PropTypes.func,
  canEdit: PropTypes.bool,
};

export default RequisitionDetailsData;
